import mixpanel from "mixpanel-browser";
import { isAnalyticsDebugEnabled } from "utils/environment";
import { getImpersonatedUserId } from "hooks/useImpersonation/useImpersonation";

export const PENDING_LOGIN_METHOD_KEY = "oko_pending_login_method";

let initialized = false;
let identified = false;

const isEnabled = () => initialized;

export const initAnalytics = () => {
  if (initialized) return;
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;
  if (!token) return;
  mixpanel.init(token, {
    track_pageview: false,
    persistence: "localStorage",
    ignore_dnt: false,
  });
  initialized = true;
};

// Profile labels match the school admin form rather than the raw v2/v3.
const GROUPS_EXPERIENCE_LABELS = { v3: "Curriculum v3" };
const CCSS_DIFFERENTIATION = "CCSS_DIFFERENTIATION";

const groupsExperienceOf = (config) =>
  GROUPS_EXPERIENCE_LABELS[config?.groups_ui_variant] || "Standard";

const curriculumModeOf = (config) => {
  if (config?.curriculum_kind === CCSS_DIFFERENTIATION) return "CCSS Differentiation";
  return config?.curriculum_id ? "Curriculum" : "Standards Only";
};

export const identifyTeacher = ({ okoUserId, email, name, schoolName, client, config }) => {
  if (!isEnabled() || !okoUserId) return;
  const distinctId = String(okoUserId);
  mixpanel.identify(distinctId);
  if (isAnalyticsDebugEnabled()) console.info(`[mixpanel] identify ${distinctId}`);
  mixpanel.people.set({
    $email: email,
    $name: name,
    school_name: schoolName,
    client: client || "Unknown",
    role: "teacher",
    groups_experience: groupsExperienceOf(config),
    curriculum_mode: curriculumModeOf(config),
  });
  identified = true;
};

export const resetAnalytics = () => {
  if (!isEnabled()) return;
  mixpanel.reset();
  if (isAnalyticsDebugEnabled()) console.info("[mixpanel] reset");
  identified = false;
};

export const isIdentified = () => identified;

const cleanBag = (properties) =>
  Object.fromEntries(
    Object.entries(properties).filter(([, v]) => v !== undefined && v !== null)
  );

const track = (event, properties = {}) => {
  if (!isEnabled()) {
    if (isAnalyticsDebugEnabled()) {
      console.info(
        `[mixpanel] (not sent) ${event} ${JSON.stringify(cleanBag(properties))} reason=not_initialized`
      );
    }
    return;
  }
  // Stamped here so no call site can forget it. It must be present as `false`,
  // not absent, when nobody is impersonated: cleanBag drops only undefined and
  // null, so never turn it into a truthiness check.
  const withMarker = { ...properties, is_impersonating: Boolean(getImpersonatedUserId()) };
  const clean = cleanBag(withMarker);
  if (isAnalyticsDebugEnabled()) {
    console.info(`[mixpanel] ${event} ${JSON.stringify(clean)}`);
  }
  mixpanel.track(event, clean);
};

const LOGIN_METHOD_LABELS = {
  google: "Google",
  clever: "Clever",
  classlink: "ClassLink",
  microsoft: "Microsoft",
  email: "Email",
};

export const formatLoginMethod = (raw) => {
  if (!raw) return "Unknown";
  return LOGIN_METHOD_LABELS[String(raw).toLowerCase()] || "Unknown";
};

export const trackLogin = ({ method, userId, email }) =>
  track("Login", { method: formatLoginMethod(method), user_id: userId, email });

export const trackViewedPage = (pageName, extras = {}) =>
  track("Viewed Page", { page_name: pageName, ...extras });

export const trackClickedButton = (buttonName, extras = {}) =>
  track("Clicked Button", { button_name: buttonName, ...extras });
